/**
 * HeadersEditorField — key/value pair editor for webhook request headers.
 * Extracted from webhook-fields.tsx to keep that file under ~200 lines.
 */
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Plus, X } from 'lucide-react';

interface Props {
  headers: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
}

export function HeadersEditorField({ headers, onChange }: Props) {
  const pairs = Object.entries(headers);

  const updateKey = (oldKey: string, newKey: string) => {
    // Skip if the new key already exists (would silently drop the existing row).
    if (newKey !== oldKey && Object.prototype.hasOwnProperty.call(headers, newKey)) return;
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(headers)) {
      next[k === oldKey ? newKey : k] = v;
    }
    onChange(next);
  };

  const updateVal = (key: string, newVal: string) => {
    onChange({ ...headers, [key]: newVal });
  };

  const removeRow = (key: string) => {
    const next = { ...headers };
    delete next[key];
    onChange(next);
  };

  const addRow = () => {
    let n = 1;
    while (headers[`Header-${n}`] !== undefined) n++;
    onChange({ ...headers, [`Header-${n}`]: '' });
  };

  return (
    <Field label="Headers (optional)" description="Custom request headers sent with the webhook.">
      {() => (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)', maxWidth: 480 }}>
          {pairs.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Input
                value={k}
                onChange={(e) => updateKey(k, e.target.value)}
                placeholder="Header-Name"
                style={{ flex: '0 0 180px' }}
                aria-label="Header name"
              />
              <Input
                value={v}
                onChange={(e) => updateVal(k, e.target.value)}
                placeholder="value"
                style={{ flex: 1 }}
                aria-label="Header value"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeRow(k)}
                aria-label={`Remove header ${k}`}
                style={{ padding: '0 var(--vn-space-1)', color: 'var(--vn-fg-muted)', flexShrink: 0 }}
              >
                <X size={13} />
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            startIcon={<Plus size={13} />}
            onClick={addRow}
            style={{ alignSelf: 'flex-start' }}
          >
            Add header
          </Button>
        </div>
      )}
    </Field>
  );
}
